import React, { useEffect, useState } from "react";
import { api, IVehicleManagerIssuesAtLocationInput, IIssueAtLocationOutput, IssueStatus } from "../../../../logic/api";
import styles from "../../styles";
import { PagedDataView } from "../../../../components/lists/PagedDataView/PagedDataView";
import { useTranslation } from "react-i18next";
import { ReleaseFlag, useReleaseManagement } from "../../../../utility/feature-management";
import IssueListItem from "./IssueListItem";
import IssueRow from "./IssueRow";
import IssueStatusFilter from "./IssueStatusFilter";
import IssueDetailDialog from "./IssueDetailDialog";
import HeaderRow from "../condensedLayout/HeaderRow";
import { Box } from "@mui/material";

const IssuesAtLocation: React.FunctionComponent<{
  isCondensedView?: boolean;
  path: string;
}> = (props) => {
  const { t } = useTranslation();
  const isFeatureEnabled = useReleaseManagement(ReleaseFlag.releaseAuditIssueManagement);

  const [statusFilter, setStatusFilter] = useState<IssueStatus[]>([IssueStatus.Open, IssueStatus.Assigned]);
  const [selectedIssue, setSelectedIssue] = useState<IIssueAtLocationOutput | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);

  const [params, setParams] = useState<IVehicleManagerIssuesAtLocationInput>({
    path: props.path,
    statusFilter: statusFilter
  });

  useEffect(() => {
    if (props.path != params.path) {
      setParams({
        ...params,
        path: props.path,
        statusFilter: statusFilter
      });
    }
  }, [props.path, statusFilter]);

  const handleStatusFilterChange = (newStatusFilter: IssueStatus[]) => {
    setStatusFilter(newStatusFilter);
    setParams({
      ...params,
      statusFilter: newStatusFilter
    });
  };

  const handleIssueClick = (issue: IIssueAtLocationOutput) => {
    setSelectedIssue(issue);
    setIsDetailDialogOpen(true);
  };

  const handleDetailDialogClose = () => {
    setIsDetailDialogOpen(false);
    setSelectedIssue(null);
  };

  const handleStatusUpdate = () => {
    // this is needed to refresh the list after status update
    setParams({ ...params });
    setIsDetailDialogOpen(false);
    setSelectedIssue(null);
  };

  if (!isFeatureEnabled) {
    return null;
  }

  return <Box>
    <Box style={{ marginBottom: 16 }}>
      <IssueStatusFilter
        selectedStatuses={statusFilter}
        onStatusChange={handleStatusFilterChange}
      />
    </Box>

    {props.isCondensedView ? (
      <PagedDataView<IVehicleManagerIssuesAtLocationInput, IIssueAtLocationOutput>
        apiHook={api.hooks.vehicleManager.getIssuesAtLocation}
        parameters={params}
        header={() => <HeaderRow columnHeaders={[
          t("locations.vehicleIssues.fields.issueType"),
          t("locations.vehicleIssues.fields.location"),
          t("locations.vehicleIssues.fields.vehicle"),
          t("locations.vehicleIssues.fields.recordedAt"),
          t("locations.vehicleIssues.fields.expectedReturn"),
          t("general.status"),
          ""
        ]} />}
        isTable
        animate={false}
        renderItem={(item: IIssueAtLocationOutput, index) => {
          return <IssueRow issue={item} rowIndex={index} onClick={() => handleIssueClick(item)} />;
        }}
      >
        <div style={styles.noData}>
          {t("locations.vehicleIssues.messages.noIssues")}
        </div>
      </PagedDataView>
    ) : (
      <Box>
        <PagedDataView<IVehicleManagerIssuesAtLocationInput, IIssueAtLocationOutput>
          apiHook={api.hooks.vehicleManager.getIssuesAtLocation}
          parameters={params}
          renderItem={(item) => {
            return <IssueListItem issue={item} onClick={() => handleIssueClick(item)} />;
          }}
        >
          <div style={styles.noData}>
            {t("locations.vehicleIssues.messages.noIssues")}
          </div>
        </PagedDataView>
      </Box>
    )}

    <IssueDetailDialog
      issue={selectedIssue}
      open={isDetailDialogOpen}
      onClose={handleDetailDialogClose}
      onStatusUpdate={handleStatusUpdate}
    />
  </Box>
};


export default IssuesAtLocation;